"use client"

import { useState } from "react"
import Link from "next/link"
import {
  Check,
  CreditCard,
  HelpCircle,
  Sparkles,
  X,
  Zap,
} from "lucide-react"
import { Button, buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"

type Cycle = "monthly" | "yearly"

const PLANS = [
  // The only tier whose numbers are real. Every line below matches what the
  // backend actually enforces — see HANDOFF_FREE_TIER.md, where each figure
  // is derived from measured generation times. Do not "round" them here:
  // a promise on this page that the product does not keep is worse than a
  // smaller promise it does.
  {
    id: "free",
    name: "Free",
    icon: Zap,
    monthlyPrice: 0,
    yearlyPrice: 0,
    description:
      "Fully functional, on a queue shared with every other free user. Your work ends when you close the tab.",
    features: [
      { text: "Every editing and dubbing feature", included: true },
      { text: "Up to 60 lines per project",        included: true },
      { text: "150 generation credits",            included: true },
      { text: "One project at a time",             included: true },
      { text: "Video and audio",                   included: true },
      { text: "Saved projects",                    included: false },
      { text: "Priority generation",               included: false },
    ],
    cta: "Start free",
    href: "/register",
    current: false,
    highlight: false,
  },
  {
    id: "basic",
    name: "Basic",
    icon: Zap,
    monthlyPrice: 9,
    yearlyPrice: 7,
    description: "For hobbyists and casual users.",
    features: [
      { text: "20 projects",            included: true },
      { text: "200 blocks per month",   included: true },
      { text: "3 voices",               included: true },
      { text: "Standard generation",    included: true },
      { text: "CSV + SRT export",       included: true },
      { text: "Priority generation",    included: false },
      { text: "Custom voices",          included: false },
      { text: "Team workspace",         included: false },
      { text: "API access",             included: false },
    ],
    cta: "Get Basic",
    current: false,
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro",
    icon: Sparkles,
    monthlyPrice: 14,
    yearlyPrice: 11,
    description: "For professionals and active content creators.",
    features: [
      { text: "Unlimited projects",     included: true },
      { text: "500 blocks per month",   included: true },
      { text: "5 custom voices",        included: true },
      { text: "Priority generation",    included: true },
      { text: "CSV + SRT + DOCX export",included: true },
      { text: "Advanced sync editor",   included: true },
      { text: "Team workspace",         included: false },
      { text: "API access",             included: false },
      { text: "Dedicated support",      included: false },
    ],
    cta: "Upgrade to Pro",
    current: false,
    highlight: true,
  },
  {
    id: "studio",
    name: "Studio",
    icon: CreditCard,
    monthlyPrice: 24,
    yearlyPrice: 19,
    description: "For studios, agencies and large teams.",
    features: [
      { text: "Unlimited projects",     included: true },
      { text: "Unlimited blocks",       included: true },
      { text: "Unlimited voices",       included: true },
      { text: "Priority generation",    included: true },
      { text: "All export formats",     included: true },
      { text: "Advanced sync editor",   included: true },
      { text: "Team workspace",         included: true },
      { text: "API access",             included: true },
      { text: "Dedicated support",      included: true },
    ],
    cta: "Get Studio",
    current: false,
    highlight: false,
  },
]

// Every answer here describes what the product does today. The previous set
// came from the design mockup and promised a payment processor that isn't
// connected, a Pro trial that doesn't exist, and a monthly block quota the
// backend has never had.
const FAQ = [
  {
    q: "What does the free plan include?",
    a: "One project of up to 60 lines, 150 generation credits, and every feature the studio has — nothing is locked.",
  },
  {
    q: "What is a credit?",
    a: "Credits are spent when you generate a dubbed take. Most lines cost one credit, and a longer line costs a little more. Regenerating a line spends another.",
  },
  {
    q: "Why did my free project disappear?",
    a: "Free projects aren't saved — they exist while the tab is open and are removed afterwards. Download anything you want to keep before you leave.",
  },
  {
    q: "How long does generation take?",
    a: "A line usually takes a few seconds. On the free plan the queue is shared, so at busy times you may wait, and generation isn't available around the clock.",
  },
  {
    q: "Can I subscribe now?",
    a: "Not yet. Paid plans are being finished — the free plan is fully working in the meantime, and the prices above are what they will cost.",
  },
]

export function PricingPage() {
  const [cycle, setCycle] = useState<Cycle>("monthly")
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-7xl space-y-16 px-4 py-10">

        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-medium text-primary">
            <CreditCard className="size-3.5" />
            Simple, transparent pricing
          </div>
          <h1 className="text-4xl font-bold tracking-tight">
            Choose your plan
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Start free, scale when ready. No hidden fees.
          </p>

          {/* Billing cycle toggle */}
          <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-card p-1">
            {(["monthly", "yearly"] as Cycle[]).map((c) => (
              <button
                key={c}
                onClick={() => setCycle(c)}
                className={cn(
                  "rounded-md px-4 py-1.5 text-sm font-medium transition-colors capitalize",
                  cycle === c
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {c}
                {c === "yearly" && (
                  <span className="ml-2 rounded-full bg-primary/20 px-2 py-0.5 text-[10px] text-primary">
                    -20%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan) => {
            const Icon = plan.icon
            const price = cycle === "monthly" ? plan.monthlyPrice : plan.yearlyPrice
            return (
              <div
                key={plan.id}
                className={cn(
                  "relative flex flex-col rounded-2xl border p-6 transition-colors",
                  plan.highlight
                    ? "border-primary bg-primary/5 shadow-[0_0_40px_-10px_rgba(var(--primary)/0.3)]"
                    : "border-border bg-card",
                )}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground">
                      Most popular
                    </span>
                  </div>
                )}

                {/* Plan name + icon. The description sits BELOW this row, in
                    a block of its own with a floor on its height — inside the
                    row it made every card's price, button and feature list
                    start at a different vertical position, since the plans'
                    descriptions differ in length by several lines. */}
                <div className="mb-3 flex items-center gap-3">
                  <div className={cn(
                    "flex size-9 items-center justify-center rounded-lg",
                    plan.highlight ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground",
                  )}>
                    <Icon className="size-5" />
                  </div>
                  <p className="font-semibold">{plan.name}</p>
                </div>
                <p className="mb-4 min-h-16 text-xs leading-relaxed text-muted-foreground">
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-6 flex items-end gap-1">
                  {price === 0 ? (
                    <span className="text-4xl font-bold">Free</span>
                  ) : (
                    <>
                      <span className="text-4xl font-bold">${price}</span>
                      <span className="mb-1 text-sm text-muted-foreground">/ mo</span>
                    </>
                  )}
                </div>

                {/* CTA — a real link where there is somewhere to go, and a
                    disabled button where there is not. The paid tiers have
                    no checkout yet; showing them as pressable would promise
                    something the product cannot do. */}
                {plan.href ? (
                  <Link
                    href={plan.href}
                    className={cn(
                      buttonVariants({
                        variant: plan.highlight ? "default" : "secondary",
                      }),
                      "mb-6 w-full",
                    )}
                  >
                    {plan.cta}
                  </Link>
                ) : (
                  <Button
                    className="mb-6 w-full"
                    variant={plan.highlight ? "default" : "secondary"}
                    disabled
                    title="Coming soon"
                  >
                    {plan.cta}
                  </Button>
                )}

                {/* Features */}
                <ul className="space-y-2.5">
                  {plan.features.map((f) => (
                    <li key={f.text} className={cn("flex items-center gap-2.5 text-sm", !f.included && "opacity-40")}>
                      {f.included
                        ? <Check className="size-4 shrink-0 text-primary" />
                        : <X className="size-4 shrink-0" />
                      }
                      {f.text}
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>

        {/* FAQ */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-center">Frequently asked questions</h2>
          <div className="space-y-2">
            {FAQ.map((item, i) => (
              <div key={i} className="rounded-xl border border-border bg-card overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-medium hover:bg-secondary/40 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <HelpCircle className="size-4 shrink-0 text-primary" />
                    {item.q}
                  </div>
                  <span className={cn("text-muted-foreground transition-transform duration-200", openFaq === i && "rotate-45")}>
                    +
                  </span>
                </button>
                {openFaq === i && (
                  <div className="border-t border-border/60 px-5 py-4 text-sm text-muted-foreground">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}
